import {
  HomeIcon,
  TruckIcon,
  DocumentChartBarIcon,
  CubeIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  CalendarIcon,
  ChatBubbleLeftIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  MapIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  SignalIcon,
  MagnifyingGlassIcon,
  LifebuoyIcon,
  CurrencyDollarIcon,
  ArrowUpTrayIcon,
  EnvelopeIcon,
  BellAlertIcon,
  ChartBarSquareIcon,
  CpuChipIcon,
  ArchiveBoxIcon,
  SparklesIcon,
  ShieldExclamationIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/vue/24/outline'
import { ContainerIcon, ShipIcon } from 'lucide-vue-next'

export interface NavigationItem {
  name: string
  icon: any
  to: string
  module: string
  badge?: {
    text: string
    type: 'primary' | 'warning' | 'success' | 'error'
  }
}

export interface NavigationSection {
  title: string
  items: NavigationItem[]
}

export const navigationSections: NavigationSection[] = [
  {
    title: 'Principal',
    items: [
      { 
        name: 'Dashboard', 
        icon: HomeIcon, 
        to: '/dashboard',
        module: 'dashboard'
      },
      { 
        name: 'Analytics', 
        icon: ChartBarIcon, 
        to: '/dashboard/analytics',
        module: 'analytics'
      },
    ]
  },
  {
    title: 'Operaciones',
    items: [
      { 
        name: 'Tracking Universal', 
        icon: MagnifyingGlassIcon, 
        to: '/dashboard/tracking-request',
        module: 'tracking'
      },
      { 
        name: 'Embarques', 
        icon: ShipIcon, 
        to: '/dashboard/shipments',
        module: 'shipments',
        badge: { text: '12', type: 'primary' }
      },
      { 
        name: 'Buzón', 
        icon: EnvelopeIcon, 
        to: '/dashboard/emails',
        module: 'emails'
      },
      { 
        name: 'Cotizaciones', 
        icon: CurrencyDollarIcon, 
        to: '/dashboard/quotes',
        module: 'quotes',
      },
      { 
        name: 'Órdenes de Compra', 
        icon: ShoppingCartIcon, 
        to: '/dashboard/purchase-orders',
        module: 'purchase-orders'
      },
      { 
        name: 'Solicitudes de Retiro', 
        icon: TruckIcon, 
        to: '/dashboard/pickup-request',
        module: 'pickup'
      },
      { 
        name: 'Inventario', 
        icon: CubeIcon, 
        to: '/dashboard/inventory',
        module: 'inventory',
        badge: { text: '3', type: 'warning' }
      },
      {
        name: 'Almacenes',
        icon: BuildingOfficeIcon,
        to: '/dashboard/warehouses',
        module: 'warehouses'
      },
      {
        name: 'Warehouse',
        icon: ArchiveBoxIcon,
        to: '/dashboard/warehouse-receipts',
        module: 'warehouse'
      },
      {
        name: 'Rutas',
        icon: MapIcon,
        to: '/dashboard/routes',
        module: 'routes'
      },
      {
        // Acceso directo temporal por cliente (flag showControllTower en Companies/{id}):
        // abre el último dashboard guardado de la Torre de Control del Asistente AI.
        name: 'Torre de Control de Riesgo',
        icon: ShieldExclamationIcon,
        to: '/dashboard/control-tower',
        module: 'control-tower'
      },
      {
        // Acceso directo por cliente (flag showAuditReport en Companies/{id}):
        // último reporte guardado de Auditoría de Órdenes; se genera si no existe.
        name: 'Auditoría de Órdenes',
        icon: ClipboardDocumentCheckIcon,
        to: '/dashboard/order-audit',
        module: 'order-audit'
      },
      {
        // Acceso directo por cliente (flag showContainerReport en Companies/{id}):
        // último reporte guardado de Control de Contenedores; se genera si no existe.
        name: 'Control de Contenedores',
        icon: ContainerIcon,
        to: '/dashboard/container-control',
        module: 'container-control'
      }
    ]
  },
  {
    title: 'Gestión',
    items: [
      {
        name: 'Asistente AI',
        icon: SparklesIcon,
        to: '/dashboard/assistant',
        module: 'assistant'
      },
      {
        name: 'DocGen AI',
        icon: DocumentTextIcon,
        to: '/dashboard/docgen',
        module: 'docgen'
      },
      {
        name: 'Dashboards',
        icon: ChartBarSquareIcon,
        to: '/dashboard/docgen/dashboards',
        module: 'dashboards'
      },
      {
        name: 'Reportes',
        icon: DocumentChartBarIcon,
        to: '/dashboard/reports/my-reports',
        module: 'reports'
      },
      { 
        name: 'Calendario', 
        icon: CalendarIcon, 
        to: '/dashboard/calendar',
        module: 'calendar',
        badge: { text: '2', type: 'success' }
      },
      { 
        name: 'Tareas', 
        icon: ClipboardDocumentListIcon, 
        to: '/dashboard/tasks',
        module: 'tasks',
        badge: { text: '5', type: 'error' }
      },
      { 
        name: 'Alertas', 
        icon: BellAlertIcon, 
        to: '/dashboard/alerts',
        module: 'alerts'
      }
    ]
  },
  {
    title: 'Uso y Métricas',
    items: [
      {
        name: 'Procesamiento',
        icon: CpuChipIcon,
        to: '/dashboard/data-processing-usage',
        module: 'usage-analytics'
      },
    ]
  },
  {
    title: 'Colaboración',
    items: [
      { 
        name: 'Mensajes', 
        icon: ChatBubbleLeftIcon, 
        to: '/dashboard/messages',
        module: 'messages',
        badge: { text: '5', type: 'error' }
      },
      { 
        name: 'Equipo', 
        icon: UserGroupIcon, 
        to: '/dashboard/team',
        module: 'team'
      }
    ]
  },
  {
    title: 'Sistema',
    items: [
      { 
        name: 'Configuración', 
        icon: Cog6ToothIcon, 
        to: '/dashboard/settings',
        module: 'settings'
      },
      { 
        name: 'Importar Datos', 
        icon: ArrowUpTrayIcon, 
        to: '/dashboard/settings/data-import',
        module: 'import_data'
      },
      {
        name: 'Monitoreo',
        icon: SignalIcon,
        to: '/dashboard/health',
        module: 'health',
        badge: { text: '3', type: 'warning' }
      }
    ]
  }, 
  {
    title: 'Support',
    items: [
      {
        name: 'Support',
        icon: LifebuoyIcon,
        to: '/dashboard/support',
        module: 'support'
      }
    ]
  }
]

/** Todos los items de navegación, sin agrupar por sección. */
export const navigationItems: NavigationItem[] = navigationSections.flatMap(
  (section) => section.items
)

const DASHBOARD_HOME = '/dashboard'

/**
 * Raíces de módulo que el menú no cubre, porque su enlace apunta a una ruta más
 * profunda: Reportes va a /dashboard/reports/my-reports, así que sin esto ni el
 * constructor ni el detalle de un reporte se resolverían como 'reports'.
 *
 * Una ruta puede pertenecer a varios módulos: /dashboard/tracking-results es el
 * detalle de embarque Y el resultado del flujo de tracking, y basta tener
 * cualquiera de los dos para entrar. Quitar Tracking Universal no debe cerrar
 * el detalle de los embarques.
 */
const EXTRA_MODULE_ROOTS: Array<{ to: string; modules: string[] }> = [
  { to: '/dashboard/reports', modules: ['reports'] },
  { to: '/dashboard/tracking-results', modules: ['shipments', 'tracking'] },
  { to: '/dashboard/shipment-requests', modules: ['shipments'] }
]

/**
 * Módulos que dan acceso a una ruta, tomando el prefijo más específico:
 * /dashboard/settings/fields resuelve a ['settings']. Tener cualquiera de los
 * módulos devueltos basta para entrar.
 *
 * /dashboard solo cuenta como coincidencia exacta. Si actuara como prefijo se
 * tragaría todas las rutas que no encajan en ningún módulo y su acceso pasaría
 * a depender del permiso de 'dashboard', que no tiene nada que ver.
 *
 * Devuelve [] para las rutas sin módulo, como el perfil o la facturación, que
 * siempre son accesibles.
 */
export function modulesForPath(path: string): string[] {
  if (path === DASHBOARD_HOME) return ['dashboard']

  const roots: Array<{ to: string; modules: string[] }> = [
    ...navigationItems.map((item) => ({ to: item.to, modules: [item.module] })),
    ...EXTRA_MODULE_ROOTS
  ]

  let best: { to: string; modules: string[] } | null = null

  for (const item of roots) {
    if (item.to === DASHBOARD_HOME) continue
    if (path !== item.to && !path.startsWith(`${item.to}/`)) continue
    if (!best || item.to.length > best.to.length) best = item
  }

  return best?.modules ?? []
}

/** Primer módulo de la ruta; null si no pertenece a ninguno. */
export function moduleForPath(path: string): string | null {
  return modulesForPath(path)[0] ?? null
}
